import type { Preset } from '@studio/models/video/preset';
import { useLatestFn, useLazyRef } from '@studio/react-utils/hooks';
import type { T_Nullable, T_OptionalKeys } from '@tryiris-ai/js-types';
import { pick } from 'lodash-es';
import pSeries from 'p-series';

import type { ReactElement } from 'react';

import { useMemo } from 'react';
import { twMerge } from 'cn';
import { Header } from './header.jsx';
import { List } from './list.jsx';
import type { Props, Wiring } from './types.js';
import { useWiring } from './wiring.js';
import { raiseError } from '#~/notifications/index.js';
import { useAnalytics } from '#~/analytics/hooks/index.js';
import { EVENT_CATEGORIES, EVENT_NAMES } from '#~/analytics/constants/index.js';

/** @internal */
export const Inner = ({
  className,
  drawer,
  wiring,
  ...props
}: Omit<Props, 'deviceId'> & { wiring: Wiring }): ReactElement => {
  const { presets, selectedIds } = wiring;
  const { track } = useAnalytics();

  // Stabilize handlers.
  const handleAdd = useLatestFn(() => {
    wiring
      .handleCreatePreset()
      .then(() => {
        // Track the save image setting preset event
        track(EVENT_NAMES.SAVE_IMAGE_SETTING_PRESET, EVENT_CATEGORIES.IMAGE_TRAY);
      })
      .catch((error) => raiseError(error, { actionable: true }));
  });

  const handleRefresh = useLatestFn((presetId: Preset['id']) => {
    pSeries([
      async (): Promise<unknown> => wiring.handleResetPresetSnapshot(presetId),
      async (): Promise<unknown> => wiring.handleRefreshPresetThumbnail(presetId)
    ]).catch((error) => raiseError(error, { actionable: true }));
  });

  const handleRemove = useLatestFn((presetId: Preset['id']) => {
    wiring
      .handleDestroyPreset(presetId)
      .then(() => {
        // Track the delete image setting preset event
        track(EVENT_NAMES.DELETE_IMAGE_SETTING_PRESET, EVENT_CATEGORIES.IMAGE_TRAY, {
          presetId
        });
      })
      .catch((error) => raiseError(error, { actionable: true }));
  });

  const handleReorder = useLatestFn((presetIds: Preset['id'][]) => {
    wiring.handleReorderPresets(presetIds);
  });

  const handleSelect = useLatestFn((presetId: Preset['id']) => {
    wiring
      .handleRestorePresetId(presetId)
      .catch((error) => raiseError(error, { actionable: true }));
  });

  const handleRename = useLatestFn((presetId: Preset['id'], nextName: string) => {
    wiring
      .handleUpdatePresetName(presetId, nextName)
      .catch((error) => raiseError(error, { actionable: true }));
  });

  const handler = useLazyRef(() => ({
    onAdd: handleAdd,
    onRefresh: handleRefresh,
    onRemove: handleRemove,
    onRename: handleRename,
    onReorder: handleReorder,
    onSelect: handleSelect
  })).current;

  // Be very careful w/ memoization here!
  return (
    <div className={twMerge('flex h-full flex-col', className)} {...props}>
      <Header className='shrink-0' drawer={drawer} handler={handler} />

      <div className='grow overflow-y-auto overflow-x-hidden'>
        <List
          handler={handler}
          presets={useMemo(
            () => presets.map((µ) => pick(µ, ['id', 'name', 'thumbnail'])),
            [presets]
          )}
          selectedIds={selectedIds}
        />
      </div>
    </div>
  );
};

/** Interim guard. */
const Guard = (props: Props): T_Nullable<ReactElement> => {
  const wiring = useWiring(props.deviceId);

  return wiring ? <Inner wiring={wiring} {...props} /> : null;
};

/** `<Presets>` component. */
export const Presets = ({
  deviceId,
  ...props
}: T_OptionalKeys<Props, 'deviceId'>): T_Nullable<ReactElement> =>
  deviceId ? <Guard deviceId={deviceId} {...props} /> : null;
